import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Button } from "components/base/Button";
import { useNavigate } from "react-router-dom";
import { Video } from "lucide-react";
import { useSendVideoInvitation, useUpdateVideoRoom } from "services/video";
import { useGetFamilyDetails } from "services/advisor";
import { LocalUserChoices } from "@livekit/components-react";
import { Family } from "protogen/advisors_service_pb";
import useIsMobile from "components/hooks/useIsMobile";
import PreJoinScreen, { PreJoinScreenRef } from "./PreJoinScreen";
import Checkbox from "components/common/Checkbox";
import CopyTextField from "components/common/CopyTextField";

interface VideoCallSetupProps {
  videoRoomUuid: string;
  familyRef?: string;
  onJoin: (choices: LocalUserChoices) => void;
}

const VideoCallSetup: React.FC<VideoCallSetupProps> = ({
  videoRoomUuid,
  familyRef,
  onJoin,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [selectedInviteOption, setSelectedInviteOption] = useState<string>("");
  const [meetingLink, setMeetingLink] = useState<string>("");
  const [transcribe, setTranscribe] = useState<boolean>(true);
  const [isSendingInvite, setIsSendingInvite] = useState<boolean>(false);
  const [family, setFamily] = useState<Family | null>(null);
  const preJoinRef = useRef<PreJoinScreenRef>(null);

  const { request: sendVideoInvitation } = useSendVideoInvitation();
  const { request: getFamilyDetails } = useGetFamilyDetails();
  const { request: updateVideoRoom } = useUpdateVideoRoom();

  useEffect(() => {
    if (videoRoomUuid) {
      const meetingUrl = `${window.location.origin}/video/${videoRoomUuid}`;
      setMeetingLink(meetingUrl);
    }
  }, [videoRoomUuid]);

  useEffect(() => {
    if (familyRef) {
      const fetchFamily = async () => {
        const response = await getFamilyDetails({ familyRef });
        if (response?.family) {
          setFamily(response.family);
        }
      };
      fetchFamily();
    }
  }, [familyRef]);

  const handleInviteClient = async () => {
    if (!selectedInviteOption || !videoRoomUuid) return;

    const [memberRef, mediumType] = selectedInviteOption.split("|");
    if (!memberRef || !mediumType) return;

    setIsSendingInvite(true);
    await sendVideoInvitation({
      videoRoomUuid,
      memberRef,
      medium: mediumType,
    });

    setIsSendingInvite(false);
  };

  const handleStartCall = async () => {
    await updateVideoRoom({
      uuid: videoRoomUuid,
      transcriptionEnabled: transcribe,
    });

    if (selectedInviteOption && family) {
      await handleInviteClient();
    }

    // Get current choices from PreJoinScreen
    const choices = preJoinRef.current?.getCurrentChoices() || {
      username: "User",
      audioEnabled: true,
      videoEnabled: true,
      audioDeviceId: "",
      videoDeviceId: "",
    };

    onJoin(choices);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
        marginTop: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          maxWidth: isMobile ? "100%" : "1200px",
          maxHeight: isMobile ? "100%" : "500px",
          height: "100%",
          width: "100%",
        }}
      >
        <Box
          sx={{
            flex: isMobile ? "0 0 300px" : 1,
            backgroundColor: "#232323",
            borderRadius: "12px",
            overflow: "hidden",
            minHeight: isMobile ? "300px" : "auto",
            height: "100%",
            width: "100%",
          }}
        >
          <PreJoinScreen
            ref={preJoinRef}
            containerized={true}
            onJoin={() => {}}
            showJoinButton={false}
          />
        </Box>

        <Box
          sx={{
            width: isMobile ? "100%" : "400px",
            padding: isMobile ? "0px 24px" : "0px 32px",
            display: "flex",
            flexDirection: "column",
            flex: isMobile ? 1 : "0 0 400px",
            gap: "20px",
            justifyContent: "center",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography variant="bodyHeavy">Invite client</Typography>
            {family && (
              <FormControl fullWidth>
                <InputLabel>Send invitation to...</InputLabel>
                <Select
                  value={selectedInviteOption}
                  label="Send invitation to..."
                  onChange={(e) =>
                    setSelectedInviteOption(e.target.value as string)
                  }
                  sx={{ borderRadius: "8px" }}
                >
                  <MenuItem value="">
                    <em>No invitation</em>
                  </MenuItem>
                  {family?.familyMembers
                    .map((member) => {
                      const options = [];

                      if (member.primaryEmail) {
                        options.push(
                          <MenuItem
                            value={`${member.ref}|email`}
                            key={`${member.ref}-email`}
                          >
                            {member.displayName} (via email)
                          </MenuItem>,
                        );
                      }

                      if (member.primaryPhone) {
                        options.push(
                          <MenuItem
                            value={`${member.ref}|text`}
                            key={`${member.ref}-text`}
                          >
                            {member.displayName} (via text)
                          </MenuItem>,
                        );
                      }

                      return options;
                    })
                    .flat()}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography variant="bodyHeavy">Or share the link</Typography>
            <CopyTextField value={meetingLink} label="Meeting link" />
          </Box>

          {/* Transcribe checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={transcribe}
                onChange={(e) => setTranscribe(e.target.checked)}
              />
            }
            label="Transcribe the call for task creation"
          />

          {/* Call actions */}
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleStartCall}
              disabled={isSendingInvite}
              startIcon={<Video size={20} />}
            >
              {isSendingInvite ? "Sending invitation..." : "Start call"}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              borderColor="border"
              onClick={() => navigate("/")}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoCallSetup;
